/**
 * Pairs outbound CALL frames with their CALLRESULT / CALLERROR by messageId.
 *
 * Transport-agnostic: receives a `send(frame)` callback and exposes
 * `call(action, payload)` for the adapter. Calls `feed(frame)` on every
 * incoming JSON frame; the dispatcher dispatches it to the matching pending
 * promise (CALLRESULT/CALLERROR) or hands it to `onIncomingCall` for the
 * adapter to interpret (CSMS-initiated requests).
 */
import {
  OCPP_MESSAGE_TYPE,
  type OcppCallErrorFrame,
  type OcppCallFrame,
  type OcppCallResultFrame,
  type OcppFrame,
} from '../OcppCall';
import { OcppCallError, OcppFrameParseError, OcppTimeoutError } from '../errors';

export const DEFAULT_CALL_TIMEOUT_MS = 30_000;

export interface CallDispatcherOptions {
  readonly send: (frame: OcppFrame) => void;
  readonly onIncomingCall: (frame: OcppCallFrame) => void;
  /**
   * Optional observer fired for EVERY frame that flows through the dispatcher
   * (outbound CALL/CALLRESULT/CALLERROR + every inbound frame the dispatcher
   * routes). Hook used by the UI EventLogStore — kept as a plain callback so
   * the protocol layer stays state-agnostic.
   */
  readonly onFrameObserved?: (frame: OcppFrame, direction: 'out' | 'in') => void;
  readonly defaultTimeoutMs?: number;
  readonly newMessageId?: () => string;
  readonly setTimer?: typeof setTimeout | SetTimerLike;
  readonly clearTimer?: typeof clearTimeout | ClearTimerLike;
}

function wrapSetTimer(fn: typeof setTimeout | SetTimerLike): SetTimerLike {
  return (cb, ms) => fn(cb, ms) as TimerHandle;
}
function wrapClearTimer(fn: typeof clearTimeout | ClearTimerLike): ClearTimerLike {
  return (id) => {
    fn(id);
  };
}

interface Pending {
  readonly action: string;
  readonly resolve: (payload: Record<string, unknown>) => void;
  readonly reject: (err: Error) => void;
  readonly timer: ReturnType<typeof setTimeout>;
}

type TimerHandle = ReturnType<typeof setTimeout>;
type SetTimerLike = (callback: () => void, ms: number) => TimerHandle;
type ClearTimerLike = (handle: TimerHandle) => void;

export class CallDispatcher {
  readonly #send: (frame: OcppFrame) => void;
  readonly #onIncomingCall: (frame: OcppCallFrame) => void;
  readonly #onFrameObserved: ((frame: OcppFrame, dir: 'out' | 'in') => void) | undefined;
  readonly #defaultTimeoutMs: number;
  readonly #newMessageId: () => string;
  readonly #setTimer: SetTimerLike;
  readonly #clearTimer: ClearTimerLike;
  readonly #pending = new Map<string, Pending>();

  constructor(opts: CallDispatcherOptions) {
    this.#send = opts.send;
    this.#onIncomingCall = opts.onIncomingCall;
    this.#onFrameObserved = opts.onFrameObserved;
    this.#defaultTimeoutMs = opts.defaultTimeoutMs ?? DEFAULT_CALL_TIMEOUT_MS;
    this.#newMessageId = opts.newMessageId ?? defaultUuidV4;
    // Browser timer functions throw "Illegal invocation" when their `this`
    // gets lost — wrap them so the field can be called as a plain function
    // later (the inner `setTimeout`/`clearTimeout` calls keep `globalThis`
    // as their `this` and Chrome stays happy).
    this.#setTimer = opts.setTimer
      ? wrapSetTimer(opts.setTimer)
      : (cb: () => void, ms: number) => setTimeout(cb, ms);
    this.#clearTimer = opts.clearTimer
      ? wrapClearTimer(opts.clearTimer)
      : (id: TimerHandle) => {
          clearTimeout(id);
        };
  }

  call(
    action: string,
    payload: Record<string, unknown>,
    timeoutMs?: number,
  ): { messageId: string; response: Promise<Record<string, unknown>> } {
    const messageId = this.#newMessageId();
    const ms = timeoutMs ?? this.#defaultTimeoutMs;
    const response = new Promise<Record<string, unknown>>((resolve, reject) => {
      const timer = this.#setTimer(() => {
        this.#pending.delete(messageId);
        reject(new OcppTimeoutError(messageId, action, ms));
      }, ms);
      this.#pending.set(messageId, { action, resolve, reject, timer });
    });
    const frame: OcppCallFrame = [OCPP_MESSAGE_TYPE.CALL, messageId, action, payload];
    this.#onFrameObserved?.(frame, 'out');
    try {
      this.#send(frame);
    } catch (err) {
      this.#fail(messageId, err instanceof Error ? err : new Error(String(err)));
    }
    return { messageId, response };
  }

  /** Feed an incoming JSON frame. Throws OcppFrameParseError on garbage. */
  feed(raw: unknown): void {
    if (!Array.isArray(raw) || raw.length < 2) {
      throw new OcppFrameParseError(`not an OCPP frame: ${JSON.stringify(raw).slice(0, 64)}`);
    }
    const [type, messageId] = raw as [number, string];
    switch (type) {
      case OCPP_MESSAGE_TYPE.CALL: {
        if (raw.length !== 4 || typeof raw[2] !== 'string') {
          throw new OcppFrameParseError('malformed CALL frame');
        }
        const frame = raw as unknown as OcppCallFrame;
        this.#onFrameObserved?.(frame, 'in');
        this.#onIncomingCall(frame);
        return;
      }
      case OCPP_MESSAGE_TYPE.CALLRESULT: {
        const payload = raw[2] as Record<string, unknown>;
        const frame = raw as unknown as OcppCallResultFrame;
        this.#onFrameObserved?.(frame, 'in');
        const pending = this.#take(messageId);
        if (!pending) return; // stray CALLRESULT after timeout — silently drop
        pending.resolve(payload);
        return;
      }
      case OCPP_MESSAGE_TYPE.CALLERROR: {
        const frame = raw as unknown as OcppCallErrorFrame;
        this.#onFrameObserved?.(frame, 'in');
        const pending = this.#take(messageId);
        if (!pending) return;
        pending.reject(new OcppCallError(messageId, frame[2], frame[3], frame[4] ?? {}));
        return;
      }
      default:
        throw new OcppFrameParseError(`unknown OCPP message type: ${String(type)}`);
    }
  }

  /** Respond to a CSMS-initiated CALL. The adapter calls this once it's built the payload. */
  reply(messageId: string, payload: Record<string, unknown>): void {
    const frame: OcppCallResultFrame = [OCPP_MESSAGE_TYPE.CALLRESULT, messageId, payload];
    this.#onFrameObserved?.(frame, 'out');
    this.#send(frame);
  }

  /** Reject a CSMS-initiated CALL with a CALLERROR. */
  replyError(
    messageId: string,
    errorCode: string,
    errorDescription: string,
    errorDetails: Record<string, unknown> = {},
  ): void {
    const frame: OcppCallErrorFrame = [
      OCPP_MESSAGE_TYPE.CALLERROR,
      messageId,
      errorCode,
      errorDescription,
      errorDetails,
    ];
    this.#onFrameObserved?.(frame, 'out');
    this.#send(frame);
  }

  /** Reject every pending call — used when the transport disconnects. */
  failAll(reason: Error): void {
    for (const [messageId] of this.#pending) {
      this.#fail(messageId, reason);
    }
  }

  get pendingCount(): number {
    return this.#pending.size;
  }

  #take(messageId: string): Pending | null {
    const p = this.#pending.get(messageId);
    if (!p) return null;
    this.#pending.delete(messageId);
    this.#clearTimer(p.timer);
    return p;
  }

  #fail(messageId: string, err: Error): void {
    const p = this.#take(messageId);
    if (!p) return;
    p.reject(err);
  }
}

function defaultUuidV4(): string {
  // Browser + Node 20+ both expose crypto.randomUUID.
  if (typeof globalThis !== 'undefined' && globalThis.crypto?.randomUUID) {
    return globalThis.crypto.randomUUID();
  }
  // Fallback for environments without randomUUID — Math.random is not crypto-
  // strength but messageIds need only be unique within a session.
  const hex = (n: number) => n.toString(16).padStart(2, '0');
  const bytes = new Uint8Array(16);
  for (let i = 0; i < 16; i++) bytes[i] = Math.floor(Math.random() * 256);
  bytes[6] = ((bytes[6] as number) & 0x0f) | 0x40;
  bytes[8] = ((bytes[8] as number) & 0x3f) | 0x80;
  const b = Array.from(bytes, (x) => hex(x));
  return `${b.slice(0, 4).join('')}-${b.slice(4, 6).join('')}-${b.slice(6, 8).join('')}-${b.slice(8, 10).join('')}-${b.slice(10, 16).join('')}`;
}
